/**
 * Pure-function render-time decision for what about / emojiStatus to
 * display NOW, given a ProfileBlob and the local clock.
 *
 * Threat model:
 *   - The server never holds cleartext about/emojiStatus — they live inside
 *     the profile-key-encrypted ProfileBlob. But the server DOES relay the
 *     encrypted blob, and a hostile server could refuse to relay an updated
 *     blob, keeping every viewer pinned to an old emoji status. We defeat
 *     that by comparing `statusExpiresAt` against the LOCAL clock passed in
 *     by the caller. The server's clock is never consulted.
 *   - The writer of the blob is supposed to have validated about and
 *     emojiStatus before sealing. We re-validate at render time anyway so a
 *     malformed-but-decryptable peer blob (buggy/old client) renders as "no
 *     field" rather than garbage.
 *   - `statusExpiresAt` governs ONLY emojiStatus; `about` does not
 *     auto-expire (mirrors Signal and iOS — about is meant to be persistent).
 *
 * Mirrors: kaether_for_ios/Stoat/E2EE/ProfileStatusExpiry.swift
 */

import type { ProfileBlob } from "./ProfileBlob";
import { validateAbout, validateEmojiStatus } from "./ProfileStatusValidation";

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export interface ProfileStatusRenderedView {
  readonly about: string | null;
  readonly emojiStatus: string | null;
}

// ---------------------------------------------------------------------------
// Core render function
// ---------------------------------------------------------------------------

/**
 * Returns the about + emojiStatus the UI should display *right now* for this
 * blob, given the local `now` (unix seconds).
 *
 * Both fields are revalidated defensively. The emoji is additionally
 * suppressed once `statusExpiresAt < now` (strict less-than, mirroring iOS).
 *
 * `now` should be `Date.now() / 1000` at the call site.
 *
 * Mirrors: ProfileStatusExpiry.render(blob:at:)
 */
export function renderStatus(
  blob: ProfileBlob,
  nowSeconds: number,
): ProfileStatusRenderedView {
  // --- about ---
  let about: string | null = null;
  if (blob.about != null) {
    const result = validateAbout(blob.about);
    about = result.ok ? result.value : null;
  }

  // --- emojiStatus ---
  let emojiStatus: string | null = null;
  if (blob.emojiStatus != null) {
    if (isStatusExpired(blob, nowSeconds)) {
      emojiStatus = null;
    } else {
      const result = validateEmojiStatus(blob.emojiStatus);
      emojiStatus = result.ok ? result.value : null;
    }
  }

  return { about, emojiStatus };
}

/**
 * True iff a `statusExpiresAt` is present and strictly precedes `nowSeconds`.
 * `null` / `undefined` expiry never expires.
 *
 * Convenience for renderers that want to grey-out entries that just ticked
 * past their TTL.
 *
 * Mirrors: ProfileStatusExpiry.isStatusExpired(blob:at:)
 */
export function isStatusExpired(blob: ProfileBlob, nowSeconds: number): boolean {
  if (blob.statusExpiresAt == null) {
    return false;
  }
  return blob.statusExpiresAt < nowSeconds;
}

/**
 * Passthrough for `about` — about is NOT subject to expiry.
 * Included for symmetry and to make the "about never expires" contract
 * explicit at the call site. Applies defensive re-validation.
 */
export function renderAbout(blob: ProfileBlob): string | null {
  if (blob.about == null) return null;
  const result = validateAbout(blob.about);
  return result.ok ? result.value : null;
}
